import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useCart } from "../lib/cart";
import VendorCard from "../components/VendorCard";
import ProductCard from "../components/ProductCard";
import PostcodeFilter from "../components/PostcodeFilter";
import { MagnifyingGlass, SealCheck, Lock, Truck, MapPin, Star } from "@phosphor-icons/react";

const CATEGORIES = [
  { key: "Dry Foods", label: "Dry Foods", img: "https://images.pexels.com/photos/1393382/pexels-photo-1393382.jpeg?auto=compress&cs=tinysrgb&w=600" },
  { key: "Fresh Produce", label: "Fresh Produce", img: "https://images.pexels.com/photos/2255935/pexels-photo-2255935.jpeg?auto=compress&cs=tinysrgb&w=600" },
  { key: "Protein", label: "Protein", img: "https://images.pexels.com/photos/1927377/pexels-photo-1927377.jpeg?auto=compress&cs=tinysrgb&w=600" },
  { key: "Spices", label: "Spices", img: "https://images.pexels.com/photos/277253/pexels-photo-277253.jpeg?auto=compress&cs=tinysrgb&w=600" },
  { key: "Oils", label: "Oils & Sauces", img: "https://images.pexels.com/photos/33783/olive-oil-salad-dressing-cooking-olive.jpg?auto=compress&cs=tinysrgb&w=600" },
  { key: "Flours", label: "Flours", img: "https://images.pexels.com/photos/1253894/pexels-photo-1253894.jpeg?auto=compress&cs=tinysrgb&w=600" },
  { key: "Kits", label: "Recipe Kits", img: "https://images.pexels.com/photos/1640774/pexels-photo-1640774.jpeg?auto=compress&cs=tinysrgb&w=600" },
];

export default function Home() {
  const { postcode } = useCart();
  const [vendors, setVendors] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  const loadData = async (pc) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (pc) params.set("postcode", pc);
      const [v, p] = await Promise.all([
        api.get(`/vendors?${params.toString()}`),
        api.get(`/products?featured=true&${params.toString()}`),
      ]);
      setVendors(v.data);
      setFeatured(p.data);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[home] could not load vendors/featured:", err);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData(postcode);
  }, [postcode]);

  return (
    <div className="pb-24" data-testid="homepage">
      {/* HERO */}
      <section className="relative pt-24 md:pt-32 pb-12 md:pb-16 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-8 lg:gap-12 items-center">
            <div className="ap-rise">
              <p className="text-xs font-bold tracking-[0.18em] uppercase text-[#A94438] mb-4">Trusted by African communities in the UK</p>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95] text-[#1A1A18] mb-5">
                Order African groceries from <span className="text-[#A94438]">trusted vendors</span> near you.
              </h1>
              <p className="text-base md:text-lg text-[#5C5C54] max-w-xl leading-relaxed mb-5">
                Shop <span className="font-editorial italic">curated</span> African staples, spices, fresh produce and recipe kits — all in one basket. Verified UK vendors. Real delivery to your door.
              </p>

              <div className="max-w-md mb-2">
                <PostcodeFilter />
              </div>
              <p className="text-xs text-[#5C5C54] mb-5 ml-2">
                Enter your postcode to see vendors delivering to your area.
              </p>

              <div className="flex flex-wrap items-center gap-2 text-xs text-[#5C5C54]">
                <span className="chip-success"><SealCheck size={12} weight="fill" /> Verified vendors only</span>
                <span className="chip-success"><Lock size={12} weight="fill" /> Secure Stripe checkout</span>
                <span className="chip-success"><Truck size={12} weight="fill" /> UK-wide delivery</span>
              </div>
            </div>

            <div className="relative ap-rise">
              <div className="relative aspect-[4/5] max-h-[540px] rounded-3xl overflow-hidden bg-[#F2EFE9]">
                <img
                  src="https://images.pexels.com/photos/37099829/pexels-photo-37099829.jpeg"
                  alt="African dishes"
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-4 right-4 bg-white/95 backdrop-blur border border-[#E5E2D9] rounded-2xl p-3 shadow-[0_8px_30px_rgba(0,0,0,0.08)]">
                  <p className="text-[10px] font-bold tracking-wider uppercase text-[#5C5C54] mb-0.5">This week</p>
                  <p className="font-heading font-black text-lg">Jollof Kit</p>
                  <p className="text-xs text-[#A94438] font-semibold">Serves 6 · £18.99</p>
                </div>
                <div className="absolute bottom-4 left-4 right-4 bg-[#1A1A18]/85 backdrop-blur text-white rounded-2xl p-4">
                  <p className="text-xs text-white/70 mb-1">"A taste of home, delivered to my doorstep."</p>
                  <p className="text-xs font-semibold">— Amara, London</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 mb-12">
        <div className="bg-white border border-[#E5E2D9] rounded-2xl p-4 md:p-5 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {[
            { icon: <SealCheck size={20} weight="duotone" className="text-[#2B5329]" />, label: "Verified Vendors", sub: "Hand-picked" },
            { icon: <Truck size={20} weight="duotone" className="text-[#A94438]" />, label: "UK-wide", sub: "Nationwide shipping" },
            { icon: <Star size={20} weight="duotone" className="text-[#E4A951]" />, label: "Verified Reviews", sub: "Real buyers only" },
            { icon: <Lock size={20} weight="duotone" className="text-[#1A1A18]" />, label: "Secure Checkout", sub: "Stripe protected" },
          ].map((t) => (
            <div key={t.label} className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#F2EFE9] flex items-center justify-center flex-shrink-0">{t.icon}</div>
              <div>
                <p className="text-sm font-bold text-[#1A1A18]">{t.label}</p>
                <p className="text-[11px] text-[#5C5C54]">{t.sub}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 mb-12 md:mb-16">
        <div className="flex items-baseline justify-between mb-6">
          <div>
            <p className="text-xs font-bold tracking-[0.15em] uppercase text-[#A94438] mb-1">Shop by category</p>
            <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight">Every pantry essential</h2>
          </div>
        </div>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3 md:gap-4">
          {CATEGORIES.map((c) => (
            <Link
              key={c.key}
              to={`/products?category=${encodeURIComponent(c.key)}`}
              data-testid={`category-${c.key.toLowerCase().replace(/\s/g, "-")}`}
              className="group block rounded-2xl overflow-hidden bg-white border border-[#E5E2D9] hover:border-[#D1CEC5] transition-all hover:-translate-y-0.5"
            >
              <div className="aspect-square overflow-hidden bg-[#F2EFE9]">
                <img src={c.img} alt={c.label} loading="lazy" onError={(e) => { e.currentTarget.src = "https://images.pexels.com/photos/37099829/pexels-photo-37099829.jpeg?auto=compress&cs=tinysrgb&w=600"; }} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-3">
                <p className="font-heading font-bold text-sm md:text-base truncate">{c.label}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* VENDORS */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 mb-12 md:mb-16">
        <div className="flex items-baseline justify-between mb-6">
          <div>
            <p className="text-xs font-bold tracking-[0.15em] uppercase text-[#A94438] mb-1">Trusted by our community</p>
            <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight">Featured Vendors</h2>
            <p className="text-xs text-[#5C5C54] mt-2">
              {postcode ? (
                <>Showing vendors delivering to <span className="font-bold">{postcode}</span>. Only vendors that deliver to your area are shown.</>
              ) : (
                <>Some vendors deliver locally, others nationwide. Enter a postcode to filter.</>
              )}
            </p>
          </div>
          <Link to="/vendors" className="btn-ghost text-xs hidden md:inline-flex" data-testid="all-vendors-link">See all</Link>
        </div>
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[0, 1, 2].map((i) => <div key={i} className="h-80 bg-white rounded-2xl animate-pulse border border-[#E5E2D9]" />)}
          </div>
        ) : vendors.length === 0 ? (
          <div className="bg-white border border-[#E5E2D9] rounded-2xl p-8 text-center" data-testid="no-vendors-message">
            <p className="text-[#5C5C54] text-sm">No vendors deliver to this postcode yet. Try another, or browse all UK-wide vendors.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {vendors.slice(0, 6).map((v) => <VendorCard key={v.vendor_id} vendor={v} />)}
          </div>
        )}
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
        <div className="flex items-baseline justify-between mb-6">
          <div>
            <p className="text-xs font-bold tracking-[0.15em] uppercase text-[#A94438] mb-1">Trending</p>
            <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight">Most-loved this week</h2>
          </div>
          <Link to="/products" className="btn-ghost text-xs hidden md:inline-flex" data-testid="all-products-link">See all</Link>
        </div>
        {featured.length === 0 ? (
          <p className="text-sm text-[#5C5C54]">No featured products yet.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {featured.slice(0, 8).map((p) => <ProductCard key={p.product_id} product={p} />)}
          </div>
        )}
      </section>

      {/* MARKETPLACE NOTICE */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 mt-12">
        <div className="bg-[#F2EFE9] border border-[#E5E2D9] rounded-2xl p-5 text-center text-xs text-[#5C5C54]">
          <p><span className="font-bold text-[#1A1A18]">Heads up:</span> Items from different vendors may arrive separately. Each vendor dispatches your order from their own kitchen or pantry.</p>
        </div>
      </section>
    </div>
  );
}
